import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, STATUS_META, fmtExpected, isExpectedOverdue } from "@/lib/api";
import { PaymentStatusDot, PAY_STATUS } from "@/components/PaymentStatusPill";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Columns3, Loader2, RefreshCw, Clock, AlertTriangle, Wrench, User, Tv, X, Cake } from "lucide-react";
import { selectCls } from "@/components/erp/ErpUi";
import { Money } from "@/components/HideValues";

const COLS = [
  { k: "parado", l: "Cadastro" }, { k: "aguardando_aprovacao", l: "Aprovação" }, { k: "em_andamento", l: "Em andamento" },
  { k: "pronto", l: "Pronto" },
];
const TV_COLORS = {
  parado: { bg: "rgba(161,161,170,0.20)", border: "rgba(161,161,170,0.45)", title: "#d4d4d8" },
  aguardando_aprovacao: { bg: "rgba(245,158,11,0.20)", border: "rgba(245,158,11,0.5)", title: "#fbbf24" },
  em_andamento: { bg: "rgba(56,189,248,0.20)", border: "rgba(56,189,248,0.5)", title: "#38bdf8" },
  pronto: { bg: "rgba(34,197,94,0.20)", border: "rgba(34,197,94,0.5)", title: "#4ade80" },
};
const days = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));

function Clock24() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div data-testid="yard-tv-clock" className="text-right leading-none">
      <div className="font-mono font-black text-5xl text-zinc-100">{now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
      <div className="text-base text-zinc-400 mt-1 capitalize">{now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</div>
    </div>
  );
}

const FlagBadges = ({ o, big }) => {
  const items = [
    isExpectedOverdue(o) && { k: "late", t: "Entrega atrasada", cls: "bg-red-600/25 border-red-500 text-red-300" },
    o.on_hold_approval && { k: "approval", t: "Aguardando aprovação", cls: "bg-amber-500/20 border-amber-500 text-amber-200" },
    o.on_hold_part && { k: "part", t: "Aguardando peça", cls: "bg-sky-500/20 border-sky-500 text-sky-200" },
    o.blocked && { k: "blocked", t: `Travado${o.blocked_reason ? `: ${o.blocked_reason}` : ""}`, cls: "bg-red-500/20 border-red-500 text-red-300" },
    o.rework && { k: "rework", t: "Retrabalho", cls: "bg-orange-500/20 border-orange-500 text-orange-300" },
    o.checklist_pending && { k: "checklist", t: "Checklist pendente", cls: "bg-violet-500/20 border-violet-500 text-violet-200" },
  ].filter(Boolean);
  if (!items.length) { return null; }
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {items.map(({ k, t, cls }) => (
        <span key={k} data-testid={`yard-tag-${k}-${o.id}`} className={`rounded-full border font-bold uppercase ${cls} ${big ? "text-lg px-3" : "text-[10px] px-2"}`}>{t}</span>
      ))}
    </div>
  );
};

const AwayBadge = ({ name, big }) => (
  <span data-testid="yard-away-badge" title={name ? `Em terceiro: ${name}` : "Em terceiro"} className={`inline-flex items-center rounded-full bg-orange-500/20 border border-orange-500 text-orange-300 font-bold uppercase ${big ? "text-lg px-3 py-0.5" : "text-[10px] px-2"}`}>Em terceiro</span>
);

function Card({ o, shopName, onOpen, onDragStart, big }) {
  const late = isExpectedOverdue(o);
  const v = o.vehicle || {};
  return (
    <button type="button" draggable={!big} onDragStart={(e) => onDragStart(e, o)} onClick={() => onOpen(o)} data-testid={`yard-card-${o.id}`}
      className={`w-full text-left rounded-xl border bg-zinc-950/80 hover:border-green-brand transition-colors ${big ? "p-4 cursor-default" : "p-3 cursor-grab active:cursor-grabbing"} ${late ? "border-red-700" : "border-zinc-800"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`font-mono font-black tracking-wider text-zinc-100 ${big ? "text-3xl" : "text-lg"}`}>{v.plate || "—"}</span>
        {o.order_number && <span className={`${big ? "text-base" : "text-[11px]"} text-zinc-500`}>OS #{o.order_number}</span>}
      </div>
      <div className={`${big ? "text-xl" : "text-sm"} text-zinc-300 truncate`}>{[v.make, v.model].filter(Boolean).join(" ") || o.description || "—"}</div>
      <div className={`mt-1 ${big ? "text-lg" : "text-xs"} text-zinc-400 flex items-center gap-1 truncate`}><User className="h-3 w-3 shrink-0" /> {o.customer_name || "—"}</div>
      {o.mechanic_name && <div className={`${big ? "text-lg" : "text-xs"} text-zinc-400 flex items-center gap-1 truncate`}><Wrench className="h-3 w-3 shrink-0" /> {o.mechanic_name}</div>}
      {o.consultant_name && <div data-testid={`yard-consultant-${o.id}`} className={`${big ? "text-lg" : "text-xs"} text-zinc-500 truncate`}>Consultor: {o.consultant_name}</div>}
      {o.payment_status && <div className="mt-1"><span data-testid={`yard-pay-${o.id}`} className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase ${PAY_STATUS[o.payment_status]?.cls || ""} ${big ? "text-base px-3 py-0.5" : "text-[10px] px-2"}`}><PaymentStatusDot status={o.payment_status} size="h-2 w-2" />{PAY_STATUS[o.payment_status]?.short || o.payment_status}{o.payment_status_source === "manual" ? " · M" : ""}</span></div>}
      {o.away !== undefined && <div className="mt-1"><AwayBadge name={o.away} big={big} /></div>}
      <FlagBadges o={o} big={big} />
      <div className={`mt-2 flex items-center justify-between gap-2 ${big ? "text-base" : "text-[11px]"}`}>
        <span data-testid={`yard-days-${o.id}`} className="inline-flex items-center gap-1 text-zinc-400"><Clock className="h-3 w-3" /> {days(o.created_at)} dia(s) no pátio</span>
        {o.expected_at && <span data-testid={`yard-expected-${o.id}`} className={`inline-flex items-center gap-1 font-bold ${late ? "text-red-400" : "text-zinc-300"}`}>{late && <AlertTriangle className="h-3 w-3" />}{fmtExpected(o.expected_at)}</span>}
      </div>
      {shopName && <div className="mt-1 text-[10px] uppercase text-amber-300 truncate">{shopName}</div>}
    </button>
  );
}

function TvCard({ o, shopName }) {
  const late = isExpectedOverdue(o);
  const v = o.vehicle || {};
  return (
    <div data-testid={`yard-tv-card-${o.id}`} className={`rounded-2xl border-2 bg-zinc-950/90 p-5 ${late ? "border-red-600" : "border-zinc-800"}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono font-black tracking-wider text-zinc-50 text-5xl leading-none">{v.plate || "—"}</span>
        {o.order_number && <span className="text-2xl font-bold text-zinc-400 whitespace-nowrap">OS #{o.order_number}</span>}
      </div>
      <div className="mt-2 text-2xl text-zinc-200 truncate">{[v.make, v.model].filter(Boolean).join(" ") || o.description || "—"}</div>
      {o.mechanic_name ? (
        <div data-testid={`yard-tv-mech-${o.id}`} className="mt-3 flex items-center gap-2 text-3xl font-black truncate text-amber-300"><Wrench className="h-7 w-7 shrink-0" /> {o.mechanic_name}</div>
      ) : (
        <div data-testid={`yard-tv-mech-${o.id}`} className="mt-3 text-sm text-zinc-500">A definir</div>
      )}
      {o.away !== undefined && <div className="mt-2"><AwayBadge name={o.away} big /></div>}
      <FlagBadges o={o} big />
      <div className="mt-1 text-lg text-zinc-500 truncate">{o.consultant_name ? `Consultor: ${o.consultant_name} · ` : ""}{o.customer_name || ""}{shopName ? ` · ${shopName}` : ""}</div>
      <div className="mt-3 flex items-center justify-between gap-3 text-xl">
        <span className="inline-flex items-center gap-2 text-zinc-300"><Clock className="h-5 w-5" /> {days(o.created_at)} dia(s)</span>
        {o.expected_at && <span data-testid={`yard-tv-expected-${o.id}`} className={`inline-flex items-center gap-2 font-bold ${late ? "text-red-400" : "text-zinc-200"}`}>{late && <AlertTriangle className="h-5 w-5" />}{fmtExpected(o.expected_at)}</span>}
      </div>
    </div>
  );
}

const BIG_MAX = 4;
const TV_TOTAL_COMPACT = 5;
const GAP = 8;
const MIN_H = 56;
const shortModel = (v) => [v.make && v.make.split(" ")[0], (v.model || "").split(/[\s/]+/)[0]].filter(Boolean).join(" ");

// Rola devagar só quando o conteúdo não cabe; pausa 5 s no fim e volta ao topo.
function useAutoScroll(ref, enabled) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) { return undefined; }
    let raf, wait = Date.now() + 3000, last = performance.now(), pos = 0;
    el.scrollTop = 0;
    const tick = (t) => {
      const dt = t - last; last = t;
      const max = el.scrollHeight - el.clientHeight;
      if (max > 2 && Date.now() > wait) {
        pos += dt * 0.025;
        if (pos >= max) { pos = max; el.scrollTop = max; wait = Date.now() + 5000; setTimeout(() => { pos = 0; el.scrollTop = 0; wait = Date.now() + 3000; }, 5000); }
        else el.scrollTop = pos;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ref, enabled]);
}

function useHeight(ref) {
  const [h, setH] = useState(0);
  useEffect(() => {
    if (!ref.current) { return undefined; }
    const ro = new ResizeObserver(([e]) => setH(e.contentRect.height));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return h;
}

function TvCompact({ o, h }) {
  const late = isExpectedOverdue(o);
  const v = o.vehicle || {};
  const fs = Math.max(18, Math.min(40, h * 0.45));
  return (
    <div data-testid={`yard-tv-compact-${o.id}`} style={{ height: h }} className={`shrink-0 rounded-xl border-2 bg-zinc-950/90 px-3 flex items-center gap-3 overflow-hidden ${late ? "border-red-600" : "border-zinc-800"}`}>
      <span className="font-mono font-black tracking-wider text-zinc-50 leading-none whitespace-nowrap" style={{ fontSize: fs }}>{v.plate || "—"}</span>
      <div className="min-w-0 flex-1 leading-tight" style={{ fontSize: Math.max(13, fs * 0.5) }}>
        <div className="text-zinc-300 truncate">{shortModel(v) || o.description || "—"}{o.blocked ? <b className="text-red-400"> · TRAVADO</b> : null}{o.away !== undefined ? <b className="text-orange-300"> · TERCEIRO</b> : null}</div>
        <div className={`font-bold truncate ${o.mechanic_name ? "text-amber-300" : "text-zinc-500"}`} style={o.mechanic_name ? undefined : { fontSize: Math.max(11, fs * 0.33) }}>{o.mechanic_name || "A definir"}</div>
      </div>
      <span className={`whitespace-nowrap font-bold ${late ? "text-red-400" : "text-zinc-300"}`} style={{ fontSize: Math.max(13, fs * 0.55) }}>{days(o.created_at)}d{late ? " !" : ""}</span>
    </div>
  );
}

function TvColumn({ c, list, shopNames, scopeAll, forceCompact }) {
  const ref = useRef(null);
  const avail = useHeight(ref);
  const compact = forceCompact || list.length > BIG_MAX;
  const n = Math.max(list.length, 1);
  const fitH = compact && avail ? Math.floor((avail - GAP * (n - 1)) / n) : 0;
  const h = Math.max(MIN_H, fitH);
  useAutoScroll(ref, compact && fitH < MIN_H);
  return (
    <section data-testid={`yard-tv-col-${c.k}`} className="rounded-2xl border-2 p-3 flex flex-col min-h-0" style={{ background: TV_COLORS[c.k].bg, borderColor: TV_COLORS[c.k].border }}>
      <div className="flex items-center justify-between px-1 pb-3">
        <span className="flex items-center gap-3 text-3xl font-black uppercase" style={{ color: TV_COLORS[c.k].title }}><span className={`h-5 w-5 rounded-full ${STATUS_META[c.k].dot}`} /> {c.l}</span>
        <span className="text-4xl font-black" style={{ color: TV_COLORS[c.k].title }}>{list.length}</span>
      </div>
      <div ref={ref} data-testid={`yard-tv-body-${c.k}`} className={`flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col ${compact ? "" : "gap-4"}`} style={compact ? { gap: GAP } : undefined}>
        {compact ? list.map((o) => <TvCompact key={o.id} o={o} h={h} />) : list.map((o) => <TvCard key={o.id} o={o} shopName={scopeAll ? shopNames[o.shop_id] : null} />)}
        {list.length === 0 && <div className="text-lg text-zinc-600 text-center py-8">Vazio</div>}
      </div>
    </section>
  );
}

function StaffTicker({ orders }) {
  const [bdays, setBdays] = useState({});
  useEffect(() => {
    const md = new Date().toLocaleDateString("sv-SE").slice(5, 10);
    api.get("/employees").then(({ data }) => setBdays(Object.fromEntries(data.filter((e) => (e.birth_date || "").slice(5, 10) === md)
      .map((e) => [`${e.role === "consultor" ? "Consultor" : "Mecânico"}|${e.name}`, true])))).catch(() => setBdays({}));
  }, []);
  const people = useMemo(() => {
    const m = {};
    for (const o of orders) {
      const v = Number(o.budget?.total || 0);
      for (const [role, name] of [["Mecânico", o.mechanic_name]]) {
        if (!name) { continue; }
        const k = `${role}|${name}`;
        m[k] = m[k] || { k, name, role, value: 0, count: 0 };
        m[k].value += v; m[k].count += 1;
      }
    }
    return Object.values(m).sort((a, b) => b.value - a.value);
  }, [orders]);
  if (!people.length) { return null; }
  const reps = Math.max(1, Math.ceil(8 / people.length));
  const half = Array.from({ length: reps }).flatMap(() => people);
  const chip = (p, dup) => (
    <span key={`${p.k}-${dup}`} data-testid={dup ? undefined : `yard-staff-${p.name}`} title={p.role} className="shrink-0 inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs">
      <b className="uppercase text-zinc-100">{p.name}</b>
      {bdays[p.k] && <span data-testid={dup ? undefined : `yard-staff-bday-${p.name}`} title="Aniversário hoje" className="inline-flex items-center gap-1 rounded-full bg-amber-brand/20 border border-amber-brand px-1.5 text-[10px] font-bold uppercase text-amber-200"><Cake className="h-3 w-3" /> Aniversário</span>}
      <span className="font-bold text-green-neon tabular-nums"><Money value={p.value} /></span>
      <span className="text-zinc-400">{p.count} OS</span>
    </span>
  );
  return (
    <div data-testid="yard-staff-ticker" className="yard-marquee overflow-hidden rounded-2xl border border-card2-border bg-card2 py-2">
      <div className="yard-marquee-track flex w-max gap-3 px-3" style={{ animationDuration: `${Math.max(15, half.length * 4)}s` }}>
        {half.map((p, i) => chip(p, i < people.length ? 0 : `a${i}`))}{half.map((p, i) => chip(p, `b${i}`))}
      </div>
    </div>
  );
}

function MiniCard({ o, onOpen, onDragStart }) {
  const late = isExpectedOverdue(o);
  const v = o.vehicle || {};
  return (
    <button type="button" draggable onDragStart={(e) => onDragStart(e, o)} onClick={() => onOpen(o)} data-testid={`yard-card-${o.id}`}
      className={`w-full text-left rounded-lg border bg-zinc-950/80 hover:border-green-brand transition-colors px-2.5 py-1.5 cursor-grab ${late ? "border-red-700" : "border-zinc-800"}`}>
      <div className="flex items-center justify-between gap-2"><span className="font-mono font-black tracking-wider text-zinc-100 text-base">{v.plate || "—"}</span>
        <span className={`text-[11px] font-bold ${late ? "text-red-400" : "text-zinc-400"}`}>{days(o.created_at)}d</span></div>
      <div className="text-[11px] text-zinc-400 truncate">{shortModel(v) || o.description || "—"} · <span className={o.mechanic_name ? "text-amber-300" : "text-zinc-600"}>{o.mechanic_name || "Sem mecânico"}</span>
        {late ? <b data-testid={`yard-late-${o.id}`} className="text-red-400"> · Entrega atrasada</b> : null}{o.blocked ? <b className="text-red-400"> · Travado</b> : null}{o.rework ? <b className="text-orange-300"> · Retrab.</b> : null}{o.away !== undefined ? <b className="text-orange-300"> · Terceiro</b> : null}</div>
    </button>
  );
}

export default function OwnerYard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [mech, setMech] = useState("");
  const [shop, setShop] = useState("");
  const [over, setOver] = useState(null);
  const [tagFilter, setTagFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tv, setTv] = useState(false);
  const scopeAll = user?.shop_id === "all";
  const shopNames = useMemo(() => Object.fromEntries((user?.shops || []).map((s) => [s.id, s.name])), [user]);

  const load = useCallback(async () => {
    try {
      const [{ data }, away] = await Promise.all([api.get("/orders"), api.get("/outsourced/away").then((r) => r.data).catch(() => ({}))]);
      setOrders(data.map((o) => (o.id in away ? { ...o, away: away[o.id] || "" } : o))); setUpdatedAt(new Date());
    }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!tv) { return undefined; }
    const onKey = (e) => { if (e.key === "Escape") { setTv(false); } };
    const onFs = () => { if (!document.fullscreenElement) { setTv(false); } };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [tv]);
  const openTv = () => { setTv(true); document.documentElement.requestFullscreen?.().catch(() => {}); };
  const closeTv = () => { setTv(false); if (document.fullscreenElement) { document.exitFullscreen?.().catch(() => {}); } };

  const active = (orders || []).filter((o) => COLS.some((c) => c.k === o.status));
  const mechanics = [...new Set(active.map((o) => o.mechanic_name).filter(Boolean))].sort();
  const visible = active.filter((o) => (!mech || (mech === "__none__" ? !o.mechanic_name : o.mechanic_name === mech)) && (!shop || o.shop_id === shop) && (!payFilter || o.payment_status === payFilter) && (!tagFilter || (tagFilter === "blocked" ? o.blocked : tagFilter === "part" ? o.on_hold_part : tagFilter === "approval" ? o.on_hold_approval : tagFilter === "checklist" ? o.checklist_pending : tagFilter === "late" ? isExpectedOverdue(o) : true)));

  const ensureMechanic = async (o) => {
    if (o.mechanic_name) { return true; }
    const choice = window.prompt(`A OS ${o.vehicle?.plate || ""} ainda não tem mecânico. Escreva o nome do mecânico responsável:` + (mechanics.length ? `\nSugestões: ${mechanics.join(", ")}` : ""), "");
    const name = (choice || "").trim();
    if (!name) { toast.info("Escolha um mecânico para iniciar o atendimento"); return false; }
    try { await api.patch(`/orders/${o.id}`, { mechanic_name: name }); setOrders((p) => p.map((x) => (x.id === o.id ? { ...x, mechanic_name: name } : x))); return true; }
    catch (err) { toast.error(formatApiError(err)); return false; }
  };
  const onDragStart = (e, o) => { e.dataTransfer.setData("text/plain", o.id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = async (e, status) => {
    e.preventDefault(); setOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const o = (orders || []).find((x) => x.id === id);
    if (!o || o.status === status) { return; }
    if (status === "em_andamento" && !(await ensureMechanic(o))) { return; }
    setOrders((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/orders/${id}`, { status }); toast.success(`${o.vehicle?.plate || "OS"} → ${STATUS_META[status].short}`); }
    catch (err) { toast.error(formatApiError(err)); }
    load();
  };

  if (tv) {
    return (
      <div data-testid="yard-tv" className="force-dark fixed inset-0 z-[100] bg-zinc-950 p-6 flex flex-col gap-5 overflow-hidden">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-5xl font-black uppercase text-zinc-100">Pátio</h1>
            <div className="text-lg text-zinc-400 mt-1">{visible.filter((o) => o.status !== "parado").length} carro(s) em atendimento{updatedAt ? ` · atualizado ${updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}</div>
          </div>
          <div className="flex items-center gap-6">
            <Clock24 />
            <button data-testid="yard-tv-exit" onClick={closeTv} className="h-12 px-4 inline-flex items-center gap-2 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-300 hover:text-green-neon text-base" title="Sair (Esc)"><X className="h-5 w-5" /> Sair (Esc)</button>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-4 gap-5 min-h-0">
          {(() => { const totalTv = visible.filter((o) => o.status !== "parado").length; return COLS.filter((c) => c.k !== "parado").map((c) => (
            <TvColumn key={c.k} c={c} list={visible.filter((o) => o.status === c.k).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))} shopNames={shopNames} scopeAll={scopeAll} forceCompact={totalTv >= TV_TOTAL_COMPACT} />
          )); })()}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><Columns3 className="h-4 w-4" /> Pátio</div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Painel do pátio</h1>
          <div className="text-[11px] text-zinc-500 mt-1">{visible.length} carro(s) · atualiza a cada 1 min{updatedAt ? ` · ${updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="yard-filter-mechanic" value={mech} onChange={(e) => setMech(e.target.value)} className={`${selectCls} w-auto`}>
            <option value="">Todos os mecânicos</option><option value="__none__">Sem mecânico</option>
            {mechanics.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {scopeAll && (
            <select data-testid="yard-filter-shop" value={shop} onChange={(e) => setShop(e.target.value)} className={`${selectCls} w-auto`}>
              <option value="">Todas as unidades</option>
              {(user?.shops || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <button data-testid="yard-tv-open" onClick={openTv} className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-green-neon text-sm" title="Modo TV"><Tv className="h-4 w-4" /> Modo TV</button>
          <button data-testid="yard-refresh" onClick={load} className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-green-neon" title="Atualizar"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {orders && <StaffTicker orders={active} />}
      <div className="flex flex-wrap items-center gap-2" data-testid="yard-pay-filter">
        <span className="text-[11px] text-zinc-500 uppercase font-mono-caps">Pagamento:</span>
        {[{ k: "", l: "Todos" }, { k: "pendente", l: "Pendente" }, { k: "em_pagamento", l: "Em pagamento" }, { k: "finalizado", l: "Finalizado" }].map(({ k, l }) => (
          <button key={k || "all"} type="button" data-testid={`yard-pay-filter-${k || "all"}`} onClick={() => setPayFilter(k)}
            className={`h-8 px-3 rounded-full border text-xs font-bold ${payFilter === k ? (k ? PAY_STATUS[k].cls : "bg-green-brand text-zinc-950 border-green-brand") : "bg-zinc-900 border-zinc-800 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="yard-tag-filter">
        {[{ k: "", l: "Todos" }, { k: "approval", l: "Aguardando aprovação" }, { k: "part", l: "Aguardando peça" }, { k: "blocked", l: "Travado" }, { k: "checklist", l: "Checklist pendente" }, { k: "late", l: "Atrasados" }].map(({ k, l }) => (
          <button key={k || "all"} type="button" data-testid={`yard-tag-filter-${k || "all"}`} onClick={() => setTagFilter(k)}
            className={`h-8 px-3 rounded-full border text-xs font-bold ${tagFilter === k ? "bg-green-brand text-zinc-950 border-green-brand" : "bg-zinc-900 border-zinc-800 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      {!orders ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
        <div data-testid="yard-board" className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory lg:snap-none -mx-1 px-1">
          {COLS.map((c) => {
            const list = visible.filter((o) => o.status === c.k).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            return (
              <section key={c.k} data-testid={`yard-col-${c.k}`} onDragOver={(e) => { e.preventDefault(); setOver(c.k); }} onDragLeave={() => setOver(null)} onDrop={(e) => onDrop(e, c.k)}
                className={`snap-start shrink-0 w-[82vw] sm:w-72 lg:w-auto lg:flex-1 lg:min-w-[220px] rounded-2xl border p-2 flex flex-col min-h-[60vh] max-h-[calc(100vh-200px)] ${over === c.k ? "border-green-brand bg-green-brand/5" : "border-card2-border bg-card2"}`}>
                <div className="flex items-center justify-between px-1 py-1">
                  <span className="flex items-center gap-2 text-xs font-bold uppercase text-zinc-200"><span className={`h-2 w-2 rounded-full ${STATUS_META[c.k].dot}`} /> {c.l}</span>
                  <span data-testid={`yard-count-${c.k}`} className="text-xs text-zinc-500">{list.length}</span>
                </div>
                <div data-testid={`yard-body-${c.k}`} className={`flex-1 min-h-0 overflow-y-auto ${list.length > BIG_MAX ? "space-y-1.5" : "space-y-2"}`}>
                  {list.map((o) => (list.length > BIG_MAX
                    ? <MiniCard key={o.id} o={o} onOpen={(x) => navigate(`/owner/ordens/${x.id}`)} onDragStart={onDragStart} />
                    : <Card key={o.id} o={o} shopName={scopeAll ? shopNames[o.shop_id] : null} onOpen={(x) => navigate(`/owner/ordens/${x.id}`)} onDragStart={onDragStart} />))}
                  {list.length === 0 && <div className="text-[11px] text-zinc-600 text-center py-6">Vazio</div>}
                </div>
              </section>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-zinc-500 hidden lg:block">Arraste o cartão para outra coluna para mudar o status. Carros entregues saem do quadro.</p>
    </div>
  );
}
